import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import {
  Address,
  FormItemLabelType,
  FormLabelItem,
  TDtoWrapper,
} from '@/shared/lib/forms/form-wrapper.types';
import { CoOwner, OwnershipList } from '@/shared/lib/sj-orm/models/asset.dto';
import { BaseDto, DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { PrivateDocumentCategory } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { MyFundDetails, MyFundGeneral } from '../../sj-orm/models/assets/categories';
import {
  ConfidentialityLevelType,
  FundUsageStrategyType,
  ManagementBodyType,
  ManagementStructureType,
  MyFundType,
  TaskOfTheFundType,
} from '../../sj-orm/models/assets/enums';
import { ContactType } from '../../sj-orm/models/contact.dto';
import { PersonalIdentifierType } from '../../sj-orm/models/document/personal-identifier.document.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type MyFund = MyFundFormGeneralType &
  MyFundFormDetailsType &
  MyFundOwnership &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const MyFundList = [
  {
    label: 'forms_assets:myFund.enums.fundType.revocable',
    key: MyFundType.revocable,
  },
  {
    label: 'forms_assets:myFund.enums.fundType.irrevocable',
    key: MyFundType.irrevocable,
  },
];

const TaskOfTheFundList = [
  {
    label: 'forms_assets:myFund.enums.task.assetsProtection',
    key: TaskOfTheFundType.assetsProtection,
  },
  {
    label: 'forms_assets:myFund.enums.task.assetsManagement',
    key: TaskOfTheFundType.assetsManagement,
  },
  {
    label: 'forms_assets:myFund.enums.task.assetTransfer',
    key: TaskOfTheFundType.assetTransfer,
  },
  {
    label: 'forms_assets:myFund.enums.task.confidentialityAssurance',
    key: TaskOfTheFundType.confidentialityAssurance,
  },
  {
    label: 'forms_assets:myFund.enums.task.financialOptimization',
    key: TaskOfTheFundType.financialOptimization,
  },
  {
    label: 'forms_assets:myFund.enums.task.other',
    key: TaskOfTheFundType.other,
  },
];

const ConfidentialityLevelList = [
  {
    label: 'forms_assets:myFund.enums.confidentialityLevel.openEnded',
    key: ConfidentialityLevelType.openEnded,
  },
  {
    label: 'forms_assets:myFund.enums.confidentialityLevel.closeEnded',
    key: ConfidentialityLevelType.closeEnded,
  },
];

const ManagementStructureList = [
  {
    label: 'forms_assets:myFund.enums.managementStructure.direct',
    key: ManagementStructureType.direct,
  },
  {
    label: 'forms_assets:myFund.enums.managementStructure.trustAgreement',
    key: ManagementStructureType.trustAgreement,
  },
  {
    label: 'forms_assets:myFund.enums.managementStructure.nominalOwnership',
    key: ManagementStructureType.nominalOwnership,
  },
];

const ManagementBodyList = [
  {
    label: 'forms_assets:myFund.enums.managementBody.manager',
    key: ManagementBodyType.manager,
  },
  {
    label: 'forms_assets:myFund.enums.managementBody.boardOfDirectors',
    key: ManagementBodyType.boardOfDirectors,
  },
  {
    label: 'forms_assets:myFund.enums.managementBody.managementCompany',
    key: ManagementBodyType.managementCompany,
  },
  {
    label: 'forms_assets:myFund.enums.managementBody.other',
    key: ManagementBodyType.other,
  },
];

export const FundUsageStrategyList = [
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.strategic',
    key: FundUsageStrategyType.strategic,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.personal',
    key: FundUsageStrategyType.personal,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.investment',
    key: FundUsageStrategyType.investment,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.commercial',
    key: FundUsageStrategyType.commercial,
  },
  {
    label: 'forms_assets:myFund.enums.fundUsageStrategy.other',
    key: FundUsageStrategyType.other,
  },
];

type MyFundOwnership = Omit<BaseDto, 'id'> & {
  confidentialityLevel?: ConfidentialityLevelType;
  managementStructure?: ManagementStructureType;
  managementBody?: ManagementBodyType;
  manager?: DtoId[];
  boardOfDirectors?: DtoId[];
  managementCompany?: DtoId[];
  other?: string;
  ownershipType?: string;
  owner?: DtoId;
  ownerShare?: number;
  isWithCoOwners?: boolean;
  coOwners?: CoOwner[];
  ownerDocument?: DtoId;
  otherOwnerDocuments?: DtoId[];
  fundFounder?: DtoId;
  fundFounderDocument?: DtoId;
  // otherDocuments?: DtoId[];
  contactInfoLabel?: FormItemLabelType;
  ownerAddress?: Address;
  phoneNumber?: number;
  email?: string;
};

type MyFundHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.TEXT;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  fullFundName: QuestionType.TEXT;
};

type MyFundHelpDetails = {
  fundAssets?: QuestionType.PICK_ARRAY_OF_DTO;
  taskOfTheFund?: QuestionType.SELECT_FROM_LIST;
  otherTaskOfTheFund?: QuestionType.TEXT;
  keyCommitments?: QuestionType.TEXT;
  typeOfBusiness?: QuestionType.TEXT;
  organizationLegalForm?: QuestionType.TEXT;
  fundUsageStrategy?: QuestionType.SELECT_FROM_LIST;
  strategyDescription?: QuestionType.TEXT_MULTILINE;
  otherFundUsageStrategy?: QuestionType.TEXT;
  incorporationDate?: QuestionType.DATE_TIME;
  fundDuration?: QuestionType.CHECKBOX;
  fundDurationDate?: QuestionType.DATE_TIME;
  prolongation?: QuestionType.CHECKBOX;
  prolongationDate?: QuestionType.DATE_TIME;
  taxNumber?: QuestionType.TEXT;
  taxNumberIssuedBy?: QuestionType.TEXT;
  lei?: QuestionType.TEXT;
  address?: QuestionType.ADDRESS;
  district?: QuestionType.TEXT;
  zipCode?: QuestionType.TEXT;
  city?: QuestionType.TEXT;
  street?: QuestionType.TEXT;
  buildingNumber?: QuestionType.TEXT;
  apartmentNumber?: QuestionType.TEXT;
  otherAddressInfo?: QuestionType.TEXT_MULTILINE;
  fundRestrictions?: QuestionType.CHECKBOX;
  fundRestrictionsComments?: QuestionType.TEXT_MULTILINE;
  founderRestrictions?: QuestionType.CHECKBOX;
  founderRestrictionsComments?: QuestionType.TEXT_MULTILINE;
  fundDocuments?: QuestionType.PICK_ARRAY_OF_DTO;
  annualReports?: QuestionType.PICK_ARRAY_OF_DTO;
  attachments?: QuestionType.FILE_UPLOAD;
};

type MyFundHelpOwnership = {
  confidentialityLevel?: QuestionType.SELECT_FROM_LIST;
  managementStructure?: QuestionType.SELECT_FROM_LIST;
  managementBody?: QuestionType.SELECT_FROM_LIST;
  manager?: QuestionType.PICK_ARRAY_OF_DTO;
  boardOfDirectors?: QuestionType.PICK_ARRAY_OF_DTO;
  managementCompany?: QuestionType.PICK_ARRAY_OF_DTO;
  other?: QuestionType.TEXT;
  ownershipType?: QuestionType.SELECT_FROM_LIST;
  isWithCoOwners?: QuestionType.CHECKBOX;
  coOwners?: QuestionType.CO_OWNERS;
  owner?: QuestionType.PICK_ARRAY_OF_DTO;
  ownerShare?: QuestionType.NUMBER;
  ownerDocument?: QuestionType.PICK_ARRAY_OF_DTO;
  otherOwnerDocuments?: QuestionType.PICK_ARRAY_OF_DTO;
  fundFounder?: QuestionType.PICK_ARRAY_OF_DTO;
  fundFounderDocument?: QuestionType.PICK_ARRAY_OF_DTO;
  // otherDocuments?: QuestionType.PICK_ARRAY_OF_DTO;
  address?: QuestionType.TEXT;
  phoneNumber?: QuestionType.PHONE_NUMBER;
  email?: QuestionType.EMAIL;
};

type MyFundFormGeneralType = TDtoWrapper<MyFundGeneral, MyFundHelpGeneral>;
type MyFundFormDetailsType = TDtoWrapper<MyFundDetails, MyFundHelpDetails>;
type MyFundFormOwnershipType = TDtoWrapper<MyFundOwnership, MyFundHelpOwnership>;

const MyFundFormGeneral: MyFundFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:myFund.inputs.type.label',
    tooltip: 'forms_assets:myFund.inputs.type.tooltip',
    value: MyFundType.revocable,
    items: MyFundList,
    required: true,
  },

  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:myFund.inputs.fundCapital.label',
    tooltip: 'forms_assets:myFund.inputs.fundCapital.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  fullFundName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.fullFundName.label',
    value: '',
    required: true,
  },
};

const MyFundFormDetails: MyFundFormDetailsType = {
  fundAssets: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:myFund.inputs.fundAssets.label',
    items: CollectionName.ASSETS,
    mode: 'multiple',
    order: 1,
  },
  taskOfTheFund: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:myFund.inputs.taskOfTheFund.label',
    items: TaskOfTheFundList,
    order: 2,
  },
  otherTaskOfTheFund: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.otherTaskOfTheFund.label',
    showIf: {
      taskOfTheFund: {
        value: TaskOfTheFundType.other,
      },
    },
    order: 3,
  },
  keyCommitments: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.keyCommitments.label',
    tooltip: 'forms_assets:myFund.inputs.keyCommitments.tooltip',
    order: 4,
  },
  typeOfBusiness: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.typeOfBusiness.label',
    order: 5,
  },
  organizationLegalForm: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.organizationLegalForm.label',
    order: 6,
  },
  fundUsageStrategy: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:myFund.inputs.fundUsageStrategy.label',
    tooltip: 'forms_assets:myFund.inputs.fundUsageStrategy.tooltip',
    items: FundUsageStrategyList,
    order: 7,
  },
  strategyDescription: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:myFund.inputs.strategyDescription.label',
    order: 8,
  },
  otherFundUsageStrategy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.otherFundUsageStrategy.label',
    showIf: {
      fundUsageStrategy: {
        value: FundUsageStrategyType.other,
      },
    },
    order: 9,
  },
  incorporationDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:myFund.inputs.incorporationDate.label',
    tooltip: 'forms_assets:myFund.inputs.incorporationDate.tooltip',
    order: 10,
  },
  fundDuration: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:myFund.inputs.fundDuration.label',
    order: 11,
  },
  fundDurationDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:myFund.inputs.fundDurationDate.label',
    tooltip: 'forms_assets:myFund.inputs.fundDurationDate.tooltip',
    showIf: {
      fundDuration: {
        value: true,
      },
    },
    order: 12,
  },
  prolongation: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:myFund.inputs.prolongation.label',
    order: 13,
  },
  prolongationDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:myFund.inputs.prolongationDate.label',
    tooltip: 'forms_assets:myFund.inputs.prolongationDate.tooltip',
    showIf: {
      prolongation: {
        value: true,
      },
    },
    order: 14,
  },
  taxNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.taxNumber.label',
    order: 15,
  },
  taxNumberIssuedBy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:myFund.inputs.taxNumberIssuedBy.label',
    order: 16,
  },
  lei: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.lei.label',
    tooltip: 'forms_assets:common.inputs.lei.tooltip',
    order: 17,
  },
  address: {
    type: QuestionType.ADDRESS,
    label: 'forms_assets:common.inputs.address.label',
    tooltip: 'forms_assets:common.inputs.address.tooltip',
    order: 18,
  },
  fundRestrictions: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:myFund.inputs.fundRestrictions.label',
    order: 18,
  },
  fundRestrictionsComments: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:myFund.inputs.fundRestrictionsComments.label',
    showIf: {
      fundRestrictions: {
        value: true,
      },
    },
    order: 19,
  },
  founderRestrictions: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:myFund.inputs.founderRestrictions.label',
    order: 20,
  },
  founderRestrictionsComments: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:myFund.inputs.founderRestrictionsComments.label',
    showIf: {
      founderRestrictions: {
        value: true,
      },
    },
    order: 21,
  },
  legalEntityAddressLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:myFund.inputs.legalEntityAddressLabel.label',
    order: 22,
  },
  otherAddressInfo: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:myFund.inputs.otherAddressInfo.label',
    order: 29,
  },
  attachments: baseAssetFields.attachments,
};

const MyFundFormOwnership: MyFundFormOwnershipType = {
  confidentialityLevel: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:myFund.inputs.confidentialityLevel.label',
    items: ConfidentialityLevelList,
  },
  managementStructure: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:myFund.inputs.managementStructure.label',
    items: ManagementStructureList,
  },
  managementBody: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:publicFund.inputs.managementBody.label',
    items: ManagementBodyList,
  },
  manager: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:publicFund.inputs.manager.label',
    items: CollectionName.CONTACTS,
    showIf: {
      managementBody: {
        value: ManagementBodyType.manager,
      },
    },
    dtoType: ContactType.BANK,
  },
  boardOfDirectors: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:publicFund.inputs.boardOfDirectors.label',
    items: CollectionName.CONTACTS,
    mode: 'multiple',
    showIf: {
      managementBody: {
        value: ManagementBodyType.boardOfDirectors,
      },
    },
    dtoType: ContactType.BANK,
  },
  managementCompany: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:publicFund.inputs.managementCompany.label',
    items: CollectionName.CONTACTS,
    showIf: {
      managementBody: {
        value: ManagementBodyType.managementCompany,
      },
    },
    dtoType: ContactType.BANK,
  },
  other: {
    type: QuestionType.TEXT,
    label: 'forms_assets:publicFund.inputs.other.label',
    showIf: {
      managementBody: {
        value: ManagementBodyType.other,
      },
    },
  },
  ownershipType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.ownerShipType.label',
    items: OwnershipList,
  },
  owner: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:myFund.inputs.owner.label',
    items: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    dtoType: PersonalIdentifierType.BIRTH_CERTIFICATE,
  },
  ownerShare: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:common.inputs.ownershipShare.label',
  },
  ownerDocument: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:common.inputs.ownerDocument.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    dtoType: PrivateDocumentCategory.OTHER,
  },
  otherOwnerDocuments: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:common.inputs.otherOwnerDocuments.label',
    items: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    dtoType: PersonalIdentifierType.BIRTH_CERTIFICATE,
  },
  contactInfoLabel: {
    type: FormLabelItem.label,
    label: 'Contact info',
  },
  ownerAddress: {
    type: QuestionType.ADDRESS,
    label: 'forms_assets:common.inputs.ownerAddress.label',
  },
  phoneNumber: {
    type: QuestionType.PHONE_NUMBER,
    label: 'forms_assets:common.inputs.phoneNumber.label',
  },
  email: {
    type: QuestionType.EMAIL,
    label: 'forms_assets:common.inputs.email.label',
  },
  isWithCoOwners: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:myFund.inputs.beneficiaries.label',
  },
  coOwners: {
    type: QuestionType.CO_OWNERS,
    label: 'Co-owners',
    showIf: {
      isWithCoOwners: {
        value: true,
      },
    },
  },
};

const MyFundForm = [
  MyFundFormGeneral,
  MyFundFormDetails,
  MyFundFormOwnership,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgent',
    'publicFundTaxAgent',
    'taxAgentOther',
    'taxAgentWithBrokerOther',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];
export { MyFundForm };
